// Core modules
import { Injectable } from '@angular/core';

// Third party modules
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  private pathToIdCache = new Map<string, number>();
  private campaignRequestCache = new Map<string, Observable<any>>();

  /**
   * Life cycle method
   * @param httpClient HttpClient
   */
  constructor(
    private apiService: ApiService
  ) { }

  /**
   * Remember mapping between clean URL slug and numeric campaignId
   */
  rememberCampaignSlug(urlPath: string, campaignId: number): void {
    if (urlPath && campaignId) {
      this.pathToIdCache.set(urlPath.trim().toLowerCase(), Number(campaignId));
    }
  }

  /**
   * Return campaign list
   */
  getCampaigns(lang): Observable<any> {
    return this.apiService.get(`/api/campaignsbylang?lang=${lang}&includeExternal=true`).pipe(
      tap((campaigns: any[]) => {
        if (Array.isArray(campaigns)) {
          campaigns.forEach((c) => {
            if (c && c.urlPath && c.campaignId) {
              this.rememberCampaignSlug(c.urlPath, c.campaignId);
            }
          });
        }
      })
    );
  }

  /**
   * Return campaign by id
   */
  getCampaignById(id: number, languageId?: string): Observable<any> {
    const key = languageId ? `${id}:${languageId}` : `${id}`;
    const cached = this.campaignRequestCache.get(key);
    if (cached) {
      return cached;
    }

    const requestUrl = languageId
      ? `/api/campaigns/${id}?languageId=${languageId}`
      : `/api/campaigns/${id}`;

    const request$ = this.apiService.get(requestUrl).pipe(
      tap((campaign: any) => {
        if (campaign && campaign.urlPath && campaign.campaignId) {
          this.rememberCampaignSlug(campaign.urlPath, campaign.campaignId);
        }
      }),
      catchError((err) => {
        this.campaignRequestCache.delete(key);
        throw err;
      }),
      shareReplay(1)
    );

    this.campaignRequestCache.set(key, request$);
    return request$;
  }

  /**
   * Return campaign by url path
   */
  getCampaignByUrlPath(urlPath: string): Observable<any> {
    return this.apiService.get(`/api/campaigns/campaignbyurlpath/${urlPath}`).pipe(
      tap((campaign: any) => {
        if (campaign && campaign.campaignId) {
          this.rememberCampaignSlug(campaign.urlPath || urlPath, campaign.campaignId);
        }
      })
    );
  }

  /**
   * Resolve numeric campaignId from clean URL path (e.g. "knacki" -> 115)
   */
  resolveCampaignIdByPath(urlPath: string): Observable<number | null> {
    const normalized = (urlPath || '').trim().toLowerCase();
    if (!normalized) {
      return of(null);
    }
    const cachedId = this.pathToIdCache.get(normalized);
    if (cachedId) {
      return of(cachedId);
    }
    return this.getCampaignByUrlPath(urlPath).pipe(
      map((campaign) => (campaign && campaign.campaignId ? Number(campaign.campaignId) : null)),
      catchError(() => of(null))
    );
  }
}
